import classNames from "classnames";
import { GeoPoint } from "firebase/firestore";
import { useField } from "formik";
import React, { useCallback, useEffect, useState } from "react";

interface FormikFieldHandler {
  label: string;
  name: string;
  type?:
    | "text"
    | "number"
    | "email"
    | "password"
    | "search"
    | "tel"
    | "url"
    | "percentage";
  classes?: string;
  prefix?: string;
  suffix?: string;
  hideLabel?: boolean;
  required?: boolean;
  disabled?: boolean;
  helptext?: string;
  placeholder?: string;
  inline?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const InputField = (props: FormikFieldHandler) => {
  let {
    label,
    type,
    name,
    classes,
    prefix,
    suffix,
    hideLabel,
    inline,
    onChange: propOnChange,
  } = props;
  let [field, meta] = useField(props);

  let fieldType = type === "percentage" ? "number" : type ?? "text";

  if (type === "percentage") {
    suffix = "%";
  }

  let onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.currentTarget.value;
      let valueToSave: string | number = value;

      if (typeof value === "string" && value) {
        if (type === "text") {
          if (prefix && !value.startsWith(prefix)) {
            valueToSave = prefix + valueToSave;
          }

          if (suffix && !value.endsWith(suffix)) {
            valueToSave = valueToSave + suffix;
          }
        }
      }

      if (typeof value === "number" && value) {
        if (type === "number") {
          valueToSave = Number(value);
        } else if (type === "percentage") {
          valueToSave = Number(value) / 100;
        }
      }

      field.onChange({
        target: {
          name: field.name,
          value: valueToSave,
        },
      });

      if (propOnChange) {
        propOnChange(e);
      }
    },
    [field, suffix, prefix, type, propOnChange]
  );

  // Remove Prefix and Suffix from value whilst editing
  let valueOrTimestampOrGeoPoint: string | number | GeoPoint =
    field.value || "";

  if (valueOrTimestampOrGeoPoint) {
    if (typeof valueOrTimestampOrGeoPoint === "object") {
      valueOrTimestampOrGeoPoint = `${valueOrTimestampOrGeoPoint.latitude}, ${valueOrTimestampOrGeoPoint.longitude}`;
    } else if (typeof valueOrTimestampOrGeoPoint === "string") {
      if (prefix && field.value.startsWith(prefix)) {
        valueOrTimestampOrGeoPoint = field.value.slice(prefix.length);
      }
      if (suffix && field.value.endsWith(suffix)) {
        valueOrTimestampOrGeoPoint = (
          valueOrTimestampOrGeoPoint as string
        ).slice(0, -suffix.length);
      }
    } else if (typeof valueOrTimestampOrGeoPoint === "number") {
      if (type === "percentage") {
        valueOrTimestampOrGeoPoint = (field.value as number) * 100;
      }
    }
  }
  let value =
    fieldType === "number"
      ? (valueOrTimestampOrGeoPoint as number)
      : (valueOrTimestampOrGeoPoint as string);

  let [inputValue, setInputValue] = useState(value ?? "");
  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  return (
    <div
      className={classNames(
        classes,
        inline ? "flex flex-row items-center justify-start gap-x-2" : "w-full"
      )}
    >
      <label
        htmlFor={name}
        className={classNames(
          hideLabel
            ? "sr-only"
            : inline
            ? "text-sm text-gray-900"
            : "block text-sm font-medium leading-6 text-gray-900 "
        )}
      >
        {formatLabel(label)}
        {props.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="mt-1 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-martEye-400">
        {prefix && (
          <span className="flex select-none items-center pl-3 text-gray-500 sm:text-sm">
            {prefix}
          </span>
        )}
        <input
          {...field}
          {...props}
          onChange={onChange}
          // @ts-ignore
          autocomplete="test"
          value={inputValue}
          type={fieldType}
          className={classNames(
            "block flex-1 border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 min-h-[40px] disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed disabled:opacity-50",
            prefix ? "pl-0.5" : "pl-3"
          )}
          disabled={props.disabled}
        />
        {suffix && (
          <span className="flex select-none items-center pr-3 text-gray-500 sm:text-sm">
            {suffix}
          </span>
        )}
      </div>
      {props.helptext && (
        <p className="mt-2 text-sm text-gray-500">{props.helptext}</p>
      )}

      {meta.error && (
        <div className="text-sm text-danger-500 leading-tight mt-1">
          {meta.error}
        </div>
      )}
    </div>
  );
};

export default InputField;

// Helper function to get the value of a field
function formatLabel(str: string) {
  // Add space between numbers and uppercase every word
  const formattedStr = str
    .replace(/(\d+)([a-zA-Z])/g, "$1 $2")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([a-zA-Z])(\d+)/g, "$1 $2")
    .replace(/\b\w/g, (match) => match.toUpperCase());

  // Return the formatted string
  return formattedStr;
}
